import * as React from "react";
import { useNavigate } from "react-router";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { apiUrl } from "../../utils/api";

/**
 * Dialog für „Risiko zu bestehendem KFZ-Vertrag hinzufügen".
 *
 * - Option A: existierendes KFZ-Risiko des Halters wählen → POST
 *   /contracts/:id/risk-objects mit { riskObjectId } → schließen + Reload.
 * - Option B: neues Fahrzeug anlegen → navigate(/risks/new?attachToContract=…).
 *
 * Pure makeStyles + plain HTML, identisch zum Stil von NewContractDialog.
 */

type AvailableRisk = {
  id: number;
  name?: string | null;
  riskType?: string | null;
  contractID?: number | null;
  characteristics?: Array<{ nameBezeichnung?: string; riskCharacteristicValue?: string }>;
};

const useStyles = makeStyles({
  overlay: {
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: "rgba(20,8,20,0.55)",
    backdropFilter: "blur(2px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    paddingTop: "32px",
    paddingBottom: "32px",
    paddingLeft: "16px",
    paddingRight: "16px",
  },
  panel: {
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    width: "100%",
    maxWidth: "780px",
    maxHeight: "88vh",
    display: "flex",
    flexDirection: "column",
    ...shorthands.borderRadius("16px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    boxShadow: "0 24px 60px rgba(20,8,20,0.32)",
    overflow: "hidden",
  },
  head: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: "18px",
    paddingBottom: "18px",
    paddingLeft: "24px",
    paddingRight: "24px",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
    backgroundColor: "#faf6fb",
  },
  title: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.4rem",
    margin: 0,
  },
  closeBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: "transparent",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    fontWeight: 600,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.borderRadius("999px"),
    ...shorthands.border("1px", "solid", BRAND.border),
    ":hover": { ...shorthands.borderColor(BRAND.purple), backgroundColor: "#f5eef6" },
  },
  body: {
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "24px",
    paddingRight: "24px",
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
    overflowY: "auto",
  },
  actionsRow: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "10px",
    "@media (min-width: 600px)": { gridTemplateColumns: "1fr 1fr" },
  },
  actionTile: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    paddingTop: "14px",
    paddingBottom: "14px",
    paddingLeft: "16px",
    paddingRight: "16px",
    ...shorthands.borderRadius("12px"),
    ...shorthands.border("1.5px", "solid", BRAND.border),
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      backgroundColor: "#f9f3fb",
      transform: "translateY(-1px)",
    },
  },
  actionIcon: {
    width: "44px",
    height: "44px",
    flexShrink: 0,
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.borderRadius("999px"),
  },
  actionGold: {
    backgroundColor: BRAND.gold,
    color: BRAND.white,
  },
  actionLabel: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1rem",
    fontWeight: 600,
  },
  actionHint: {
    fontSize: "0.78rem",
    color: BRAND.muted,
  },
  search: {
    width: "100%",
    boxSizing: "border-box",
    fontSize: "0.92rem",
    fontFamily: BRAND.fontPrimary,
    paddingTop: "9px",
    paddingBottom: "9px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    outlineWidth: 0,
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
      boxShadow: `0 0 0 3px ${BRAND.purple}33`,
    },
  },
  list: {
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
    maxHeight: "48vh",
    overflowY: "auto",
  },
  riskRow: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    textAlign: "left",
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "14px",
    paddingRight: "14px",
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      backgroundColor: "#f9f3fb",
    },
  },
  badge: {
    width: "36px",
    height: "36px",
    flexShrink: 0,
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.borderRadius("999px"),
  },
  hint: {
    color: BRAND.muted,
    fontSize: "0.9rem",
  },
  divider: {
    height: "1px",
    backgroundColor: BRAND.borderDashed,
  },
});

const initials = (name: string) =>
  name
    .split(/\s|,/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase() || "?";

type Props = {
  open: boolean;
  contractId: number;
  contactId?: number | null;
  /** Risk IDs already attached to this contract — filtered out of the picker. */
  alreadyAttachedRiskIds?: number[];
  onClose: () => void;
  onAttached?: (riskObjectId: number) => void;
};

const riskRowIcon = (
  <svg width="20" height="14" viewBox="0 0 56 36" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 24l3-9a3 3 0 0 1 2.8-2h22.4a3 3 0 0 1 2.8 2l3 9" />
    <path d="M4 24h44v6H4z" />
  </svg>
);

const plusIcon = (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M10 4v12M4 10h12" />
  </svg>
);

const AddRiskToContractDialog: React.FC<Props> = ({
  open,
  contractId,
  contactId,
  alreadyAttachedRiskIds,
  onClose,
  onAttached,
}) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [phase, setPhase] = React.useState<"choose" | "pickExisting">("choose");
  const [risks, setRisks] = React.useState<AvailableRisk[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setPhase("choose");
      setSearch("");
      setErrorMsg(null);
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  React.useEffect(() => {
    if (!open || phase !== "pickExisting" || !contactId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(apiUrl(`risk-objects/by-contact/${contactId}`));
        const data = res.ok ? ((await res.json()) as AvailableRisk[]) : [];
        if (cancelled) return;
        const kfzOnly = (Array.isArray(data) ? data : []).filter((r) =>
          (r.riskType ?? "").toLowerCase().includes("kfz"),
        );
        setRisks(kfzOnly);
      } catch {
        if (!cancelled) setRisks([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, phase, contactId]);

  const filtered = React.useMemo(() => {
    const attached = new Set(alreadyAttachedRiskIds ?? []);
    const q = search.trim().toLowerCase();
    return risks
      .filter((r) => !attached.has(r.id))
      .filter((r) => {
        if (!q) return true;
        const name = (r.name ?? "").toLowerCase();
        const plate = String(
          (r.characteristics ?? []).find((c) => c.nameBezeichnung === "licensePlate")
            ?.riskCharacteristicValue ?? "",
        ).toLowerCase();
        const manu = String(
          (r.characteristics ?? []).find((c) => c.nameBezeichnung === "manufacturer")
            ?.riskCharacteristicValue ?? "",
        ).toLowerCase();
        return name.includes(q) || plate.includes(q) || manu.includes(q);
      });
  }, [risks, search, alreadyAttachedRiskIds]);

  const attachExisting = async (riskId: number) => {
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const res = await fetch(apiUrl(`contracts/${contractId}/risk-objects`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ riskObjectId: riskId }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      onAttached?.(riskId);
      onClose();
    } catch (e) {
      console.error(e);
      setErrorMsg("Anhängen fehlgeschlagen. Bitte später erneut versuchen.");
    } finally {
      setSubmitting(false);
    }
  };

  const createNew = () => {
    onClose();
    navigate(`/risks/new?attachToContract=${contractId}`);
  };

  if (!open) return null;

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label="Risiko zu Vertrag hinzufügen"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.panel}>
        <div className={styles.head}>
          <h2 className={styles.title}>Risiko zu Vertrag hinzufügen</h2>
          <button type="button" className={styles.closeBtn} onClick={onClose}>
            Schließen
          </button>
        </div>

        <div className={styles.body}>
          {phase === "choose" && (
            <>
              <div className={styles.hint}>
                Wählen Sie, wie das Risiko in diesen Sammel-/Rahmenvertrag aufgenommen werden soll.
              </div>
              <div className={styles.actionsRow}>
                <button
                  type="button"
                  className={styles.actionTile}
                  onClick={() => setPhase("pickExisting")}
                  disabled={!contactId}
                >
                  <span className={styles.actionIcon}>{riskRowIcon}</span>
                  <span style={{ display: "flex", flexDirection: "column" }}>
                    <span className={styles.actionLabel}>Bestehendes Risiko wählen</span>
                    <span className={styles.actionHint}>
                      Ein bereits angelegtes KFZ-Risikoobjekt des Halters anhängen.
                    </span>
                  </span>
                </button>
                <button type="button" className={styles.actionTile} onClick={createNew}>
                  <span className={mergeClasses(styles.actionIcon, styles.actionGold)}>{plusIcon}</span>
                  <span style={{ display: "flex", flexDirection: "column" }}>
                    <span className={styles.actionLabel}>Neues Fahrzeug anlegen</span>
                    <span className={styles.actionHint}>
                      Startet die Risiko-Anlage und hängt das neue Fahrzeug nach dem Speichern
                      automatisch an diesen Vertrag.
                    </span>
                  </span>
                </button>
              </div>
            </>
          )}

          {phase === "pickExisting" && (
            <>
              <input
                className={styles.search}
                type="search"
                value={search}
                placeholder="Suche nach Kennzeichen, Marke oder Risiko-Name…"
                onChange={(e) => setSearch(e.target.value)}
              />
              {errorMsg && (
                <div style={{ color: "#a1331c", fontSize: "0.88rem" }}>{errorMsg}</div>
              )}
              {loading ? (
                <div className={styles.hint}>Lade Risiken…</div>
              ) : filtered.length === 0 ? (
                <div className={styles.hint}>
                  Keine passenden KFZ-Risiken gefunden, die noch nicht im Vertrag enthalten sind.
                </div>
              ) : (
                <div className={styles.list}>
                  {filtered.map((r) => {
                    const plate = String(
                      (r.characteristics ?? []).find((c) => c.nameBezeichnung === "licensePlate")
                        ?.riskCharacteristicValue ?? "",
                    );
                    const manu = String(
                      (r.characteristics ?? []).find((c) => c.nameBezeichnung === "manufacturer")
                        ?.riskCharacteristicValue ?? "",
                    );
                    const model = String(
                      (r.characteristics ?? []).find((c) => c.nameBezeichnung === "typeName")
                        ?.riskCharacteristicValue ?? "",
                    );
                    const name = r.name || plate || `KFZ ${r.id}`;
                    const sub = [plate && plate !== name ? plate : null, [manu, model].filter(Boolean).join(" ")]
                      .filter(Boolean)
                      .join(" · ");
                    return (
                      <button
                        key={r.id}
                        type="button"
                        className={styles.riskRow}
                        disabled={submitting}
                        onClick={() => attachExisting(r.id)}
                      >
                        <span className={styles.badge}>{initials(name)}</span>
                        <span style={{ display: "flex", flexDirection: "column" }}>
                          <span style={{ fontWeight: 700 }}>{name}</span>
                          <span className={styles.hint}>{sub || "—"}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
              <div className={styles.divider} />
              <button
                type="button"
                className={styles.actionTile}
                onClick={() => setPhase("choose")}
              >
                <span className={styles.actionIcon}>
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M11 4l-5 5 5 5" />
                  </svg>
                </span>
                <span className={styles.actionLabel}>Zurück zur Auswahl</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddRiskToContractDialog;
